import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import axiosInstance from '../../api/axiosInstance'
import type {
  DevolucionOrderDetail,
  DevolucionOrderListItem
} from '../../types/devoluciones'
import { formatDevolucionDate } from '../../utils/devoluciones-utils'
import { extractAxiosApiErrorMessage } from '../../utils/apiErrorMessage'

const DEFAULT_LIMIT = 20

interface OrdersListResponse {
  data?: DevolucionOrderListItem[]
}

export function useDevolucionOrders(initialStatus?: string | readonly string[]) {
  const [orders, setOrders] = useState<DevolucionOrderListItem[]>([])
  const [selectedOrder, setSelectedOrder] = useState<DevolucionOrderDetail | null>(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(Boolean(initialStatus))
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  const statesQuery = Array.isArray(initialStatus)
    ? initialStatus.join(',')
    : initialStatus

  const fetchOrders = useCallback(async (pageNumber: number) => {
    if (!statesQuery) return

    try {
      setIsLoadingMore(true)
      const response = await axiosInstance.get<OrdersListResponse>(
        `/list?limit=${DEFAULT_LIMIT}&page=${pageNumber}&states=${statesQuery}&verTodos=true`
      )
      const rows = Array.isArray(response.data?.data) ? response.data.data : []
      const formattedRows = rows.map((order) => ({
        ...order,
        date_created: formatDevolucionDate(order.date_created)
      }))

      if (pageNumber === 1) {
        setOrders(formattedRows)
      } else {
        setOrders((current) => [...current, ...formattedRows])
      }
      setHasMore(formattedRows.length === DEFAULT_LIMIT)
    } catch (error) {
      toast.error(extractAxiosApiErrorMessage(error, 'Error al cargar pedidos de devolución'))
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [statesQuery])

  useEffect(() => {
    if (statesQuery) {
      fetchOrders(page)
    }
  }, [fetchOrders, statesQuery, page])

  const fetchOrderById = useCallback(async (orderId: number | string) => {
    const normalizedId = String(orderId).trim()
    if (!normalizedId) {
      toast.error('Ingresa el ID del pedido')
      return null
    }

    setIsSearching(true)
    try {
      const response = await axiosInstance.get<DevolucionOrderDetail>(`/list/${normalizedId}`)
      setSelectedOrder(response.data)
      return response.data
    } catch (error) {
      const message = extractAxiosApiErrorMessage(error, 'No se pudo consultar el pedido')
      toast.error(message)
      setSelectedOrder(null)
      return null
    } finally {
      setIsSearching(false)
    }
  }, [])

  return {
    orders,
    selectedOrder,
    setSelectedOrder,
    page,
    setPage,
    hasMore,
    isLoading,
    isLoadingMore,
    isSearching,
    fetchOrderById
  }
}
